<template>
  <div class="p-grid">
    <div class="p-col-12 p-pt-1 p-pb-1">
      <h2>{{ $t('energy.title') }}</h2>

      <div class="irinox-form energy">

        <h4 class="p-text-bold">{{ $t('energy.total_consumption') }}</h4>

        <div class="p-d-flex p-jc-between p-ai-center">
          <div class="p-d-flex">
            <div class="p-formgroup-inline p-ai-center">
              <div class="p-field">
                <MultiSelect v-model="selectedLabs" :options="labs" optionLabel="name" optionValue="code"
                  :maxSelectedLabels="1" :placeholder="$t('energy.filter_labs_ph')"
                  :selectedItemsLabel="'{0} '+$t('energy.filter_labs_selected')" :emptyMessage="$t('dashboard.filter_remove')"/>
              </div>
              <div class="p-field">
                <MultiSelect v-model="selectedModelNumbers" :options="modelNumbers" optionLabel="name" optionValue="code" 
                  :maxSelectedLabels="1" :placeholder="$t('energy.filter_models_ph')" 
                  :selectedItemsLabel="'{0} '+$t('energy.filter_models_selected')" :emptyMessage="$t('dashboard.filter_remove')" />
              </div>
            </div>
          </div>
          <div class="p-d-flex">
            <div class="p-formgroup-inline p-ai-center">
              <div class="p-field">
                <Dropdown id="period" v-model="periodSelected" :options="periods" optionLabel="name" optionValue="code"
                  :placeholder="$t('filter.period')" />
              </div>
              <div class="p-field">
                <SelectButton v-model="energyUnit" :options="energyUnitOptions" />
              </div>
            </div>
          </div>

        </div>

        <div class="p-grid">
          <div class="p-col-fixed" style="width: 350px;">
            <div class="p-d-flex p-flex-column p-mt-4" style="gap: 1rem;">
              <EnergyCard 
                :title="$t('energy.estimated_consumption')"
                :tooltip="$t('energy.estimated_consumption_tooltip')"
                :value="estimatedConsumption!= null ? $n(estimatedConsumption, estimatedConsumption<100 ? 'power' : 'integer') : '-'" 
                :unit="'kWh'" 
                icon="assets/irinox/icons/energy_consumption.svg"
              />
              <EnergyCard 
                :title="$t('energy.estimated_expense')"
                :tooltip="globalCurrency ? $t('energy.estimated_expense_tooltip') : $t('energy.estimated_expense_tooltip_multiple_currencies')"
                :value="estimatedExpense!=null ? (globalCurrency ? formatCurrency(estimatedExpense, globalCurrency) : 'N/A') : '-'" 
                :unit="globalCurrency ? $functions.currencies()[globalCurrency] : ''" 
                icon="assets/irinox/icons/energy_costs.svg"
              />
              <EnergyCard 
                :title="$t('energy.estimated_co2')"
                :tooltip="$t('energy.estimated_co2_tooltip')"
                :value="estimatedCO2!= null ? $n($functions.weight(estimatedCO2,isMetric), estimatedCO2<100 ? 'co2' : 'integer') : '-'" 
                :unit="isMetric?'Kg':'lb'" 
                icon="assets/irinox/icons/energy_green.png"
              />
            </div>
          </div>
          <div class="p-col">
            <EnergyChart
              :serialNumber="null" 
              :energyUnit="energyUnit" 
              :labs="selectedLabs" 
              :models="selectedModelNumbers" 
              :period="periodSelected" />
          </div>
          

        </div>




        <h4 class="p-text-bold p-mb-1">{{ $t('energy.total_consumption_per_lab') }}</h4>
        <div>{{ $t('energy.data_period') }} {{ $d($moment().subtract(periodSelected, 'days').toDate(), 'shortNumeric') }} - {{ $d(new Date(), 'shortNumeric') }}</div>

        <div v-for="(item, index) in consumptionPerLab" :key="'consumptionPerLab'+index" class="p-mt-4 consumption-per-lab">
          <div class="consumption-per-lab-title" v-if="!(index==0 && item.labName.startsWith('ZZZ|||'))">
            {{ item.labName.replace('ZZZ|||','') }}
          </div>
          <div class="consumption-per-lab-overview">
            <EnergyOverview 
              :title="$t('energy.estimated_consumption')"
              :value="item.estimatedConsumption!= null ? $n(item.estimatedConsumption, item.estimatedConsumption<100 ? 'power' : 'integer') : '-'" 
              :unit="'kWh'" 
              icon="assets/irinox/icons/energy_consumption.svg"
            />
            <EnergyOverview 
              :title="$t('energy.estimated_expense')"
              :value="item.estimatedExpense!= null ? (item.currency ? formatCurrency(item.estimatedExpense, item.currency) : 'N/A') : '-'" 
              :unit="item.currency ? $functions.currencies()[item.currency] : ''" 
              icon="assets/irinox/icons/energy_costs.svg"
            />
            <EnergyOverview 
              :title="$t('energy.estimated_co2')"
              :value="item.estimatedCO2!= null ? $n($functions.weight(item.estimatedCO2,isMetric), item.estimatedCO2<100 ? 'co2' : 'integer') : '-'" 
              :unit="isMetric?'Kg CO₂':'lb CO₂'" 
              icon="assets/irinox/icons/energy_green_small.png"
            />
          </div>
          <DataTable :value="item.appliances" stripedRows 
            responsiveLayout="stack" 
            breakpoint="960px" :selectionMode="'single'" dataKey="serialNumber" @rowSelect="onRowSelect"  
            :autoLayout="true" sortField="name" :sortOrder="1" class="p-mt-3 energy-table">

            <Column field="name" :header="$t('energy.col_appliance_name')" sortable class="highlighted"></Column>

            <Column field="modelNumber" :header="$t('energy.col_model')" sortable class="highlighted">
              <template #body="slotProps">
                {{$functions.fixModelName(slotProps.data.gamma + ' ' +slotProps.data.modelNumber)}} 
              </template>
            </Column>

            <Column field="estimatedConsumption" :header="$t('energy.col_estimated_consumption')" sortable>
              <template #body="slotProps">
                {{ slotProps.data.estimatedConsumption!=null ? $n(slotProps.data.estimatedConsumption,'power') : '-'}} kWh
              </template>
            </Column>

            <Column field="estimatedExpense" :header="$t('energy.col_estimated_expense')" sortable>
              <template #body="slotProps">
                {{ slotProps.data.estimatedExpense!=null ? formatCurrency(slotProps.data.estimatedExpense,slotProps.data.currency, true) : '-'}}
              </template>
            </Column>
            
            <Column field="unitCost" :header="$t('energy.col_unit_cost')" sortable>
              <template #body="slotProps">
                {{ slotProps.data.unitCost!=null ? formatCurrency(slotProps.data.unitCost,slotProps.data.currency, true) : '-'}}
              </template>
            </Column>

            <Column ></Column>

          </DataTable>

        </div>

      </div>
    </div>
  </div>
  
  <IrinoxDialogDark :visible="dialog.error" 
    mode="error" 
    :message="$t('energy.error_dialog')"
    :life="3000" 
    @update:visible="dialog.error = $event" />
    
</template>

<script>
import IrinoxDialogDark from '../IrinoxDialogDark.vue';
import ApplianceService from '../../service/ApplianceService';
import EnergyCard from './EnergyCard.vue';
import EnergyOverview from './EnergyOverview.vue';
import { mapGetters } from 'vuex';
import EnergyChart from './EnergyChart.vue';


export default {
  components: {
    EnergyCard,
    EnergyOverview,
    IrinoxDialogDark,
    EnergyChart,
  },
  data() {
    return {
      msConverter: 1000, // Set it to 1000 if API response is in seconds or to 1 if milliseconds
      labs: [],
      selectedLabs: [],
      modelNumbers: [],
      selectedModelNumbers: [],
      periods: [{
        code: 7,
        name: this.$t('filter.period_last_w')
      }, {
        code: 30,
        name: this.$t('filter.period_last_1m')
      }, {
        code: 60,
        name: this.$t('filter.period_last_2m')
      }, {
        code: 90,
        name: this.$t('filter.period_last_3m')
      }],
      periodSelected: 7,
      energyUnit: 'kWh',
      energyUnitOptions: ['kWh'],
      appliances: [],
      estimatedConsumption: 0,
      estimatedExpense: 0,
      estimatedCO2: 0,
      consumptionPerLab: [],
      dialog: {
        error: false,
      },
      
    };
  },
  computed: {
    ...mapGetters({
      personaltoken: 'userdata/personaltoken',
      isMetric: 'userdata/isCelsius',
    }),
    globalCurrency() {
      if(this.appliances?.length > 0) {
        let curr = this.appliances[0].currency;
        for (let i = 1; i < this.appliances.length; i++) {
          if(curr != this.appliances[i].currency) {
            return null;
          }
        }
        return curr;
      }
      return null;
    }
  },
  watch: {
    'selectedLabs': function() { this.reloadAppliances(false) },
    'selectedModelNumbers': function() { this.reloadAppliances(false) },
    'periodSelected': function() { this.reloadAppliances(false) },
  },
  created() {
    this.applianceService = new ApplianceService();
  },
  mounted() {
    this.reloadAppliances(true);
  },
  methods: {
    formatCurrency(value, currency, showUnit = false) {
      if(value == null) return '-';
      let fmt = this.$n(value, value<100 ? 'money' : 'integer', this.$functions.currencyOpt(currency));
      if(showUnit) {
        fmt +=  ' ' + this.$functions.currencies()[currency];
      }
      return fmt;
    },
    reloadAppliances(filters = false) {
      console.log("filtersAndCounters", filters);
      let startTimestamp = this.$moment().subtract(this.periodSelected, 'days').startOf('day').unix() * this.msConverter;
      this.applianceService.getApplianceEnergyList(this.personaltoken, startTimestamp, this.selectedLabs, this.selectedModelNumbers)
        .then((data) => {
          if (data.code == "200") {
            this.appliances = data.body;
            this.reloadFiltersAndCounters(filters);
            this.energyUnit = 'kWh';
            if(this.globalCurrency) {
              this.energyUnitOptions = ['kWh', this.globalCurrency];
            } else {
              this.energyUnitOptions = ['kWh'];
            }
          } else {
            console.error(JSON.stringify(data));
            this.dialog.error = true;
          }
        })
        .catch(error => {
          console.error("ERROR " + error);
          this.dialog.error = true;
        });
    },
    reloadFiltersAndCounters(filters = false) {
      let ls = [];
      let mns = [];
      this.estimatedConsumption = 0;
      this.estimatedExpense = 0;
      this.estimatedCO2 = 0;
      this.consumptionPerLab = [];

      for (let i = 0; i < this.appliances.length; i++) {

        //FILTERS
        if (this.appliances[i].labName) {
          ls.push({ 
            code: this.appliances[i].labId, 
            name: this.appliances[i].labName 
          });
        }
        if(['MF Next', 'CP', 'CK', 'EF Next'].includes(this.appliances[i].gamma)) {
          mns.push({ 
            code: this.appliances[i].modelId,
            name: this.$functions.fixModelName(this.appliances[i].gamma + ' ' + this.appliances[i].modelNumber)
          });
        }

        //COUNTERS
        this.estimatedConsumption += this.appliances[i].estimatedConsumption || 0;
        this.estimatedExpense += this.appliances[i].estimatedExpense || 0;
        this.estimatedCO2 += this.appliances[i].estimatedCO2 || 0;

        //CONSUMPTION PER LAB
        if(this.appliances[i].labName == null || this.appliances[i].labName === '') {
          this.appliances[i].labName = "ZZZ|||"+this.$t('energy.no_lab');
        }
        let consumptionPerLabIndex = this.getConsumptionPerLabIndex(this.appliances[i].labName);
        if(consumptionPerLabIndex === -1) {
          this.consumptionPerLab.push({
            labId: this.appliances[i].labId,
            labName: this.appliances[i].labName,
            appliances: [this.appliances[i]],
            estimatedConsumption: this.appliances[i].estimatedConsumption || 0,
            estimatedExpense: this.appliances[i].estimatedExpense || 0,
            estimatedCO2: this.appliances[i].estimatedCO2 || 0,
            currency: this.appliances[i].currency
          });
        } else {
          this.consumptionPerLab[consumptionPerLabIndex].appliances.push(this.appliances[i]);
          this.consumptionPerLab[consumptionPerLabIndex].estimatedConsumption += this.appliances[i].estimatedConsumption || 0;
          this.consumptionPerLab[consumptionPerLabIndex].estimatedExpense += this.appliances[i].estimatedExpense || 0;
          this.consumptionPerLab[consumptionPerLabIndex].estimatedCO2 += this.appliances[i].estimatedCO2 || 0;
          if(this.consumptionPerLab[consumptionPerLabIndex].currency != this.appliances[i].currency) {
            this.consumptionPerLab[consumptionPerLabIndex].currency = null;
          }
        }
      }

      if(filters) {
        this.labs = this.$functions.uniqByKeepFirst(ls, item => item.code).sort((a,b) => this.$functions.compareStrings(a,b,'name'));
        this.modelNumbers = this.$functions.uniqByKeepFirst(mns, item => item.code).sort((a,b) => this.$functions.compareStrings(a,b,'name'));
      }
      this.consumptionPerLab.sort((a,b) => this.$functions.compareStrings(a,b,'labName'));
    },
    getConsumptionPerLabIndex(labName) {
      for (let i = 0; i < this.consumptionPerLab.length; i++) {
        if (this.consumptionPerLab[i].labName === labName) {
          return i;
        }
      }
      return -1;
    },
    onRowSelect(event) {
      this.$router.push({ name: 'energy-details', params: { serialNumber: event.data.serialNumber } });
    }
  }
}
</script>

<style scoped>

.irinox-form .p-dropdown,
.irinox-form .p-multiselect {
  width: 200px;
}

</style>